import { lazy, type ComponentType } from 'react';

export const RELOAD_KEY = 'chunk-reload';

export function isChunkLoadError(error: unknown): boolean {
  return (
    error instanceof Error &&
    (error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Loading chunk') ||
      error.message.includes('Loading CSS chunk'))
  );
}

/**
 * Wraps React.lazy() with retry logic for failed dynamic imports.
 * After a deployment, old chunk filenames (content-hashed) may no longer
 * exist on the server. This helper retries the import once, then triggers
 * a full page reload (once) to fetch the new manifest.
 */
export function lazyWithRetry<T extends ComponentType>(factory: () => Promise<{ default: T }>) {
  return lazy(() =>
    factory()
      .then(module => {
        // Clear reload flag on successful load so future deployments
        // can still trigger an auto-reload if needed.
        sessionStorage.removeItem(RELOAD_KEY);
        return module;
      })
      .catch((error: unknown) => {
        if (!isChunkLoadError(error)) throw error;

        // Retry once — handles transient network failures.
        // If the chunk is truly gone (stale deployment), this will fail again
        // and we fall through to a full page reload.
        return factory().catch(() => {
          const hasReloaded = sessionStorage.getItem(RELOAD_KEY);
          if (!hasReloaded) {
            sessionStorage.setItem(RELOAD_KEY, '1');
            window.location.reload();
            // Keep Suspense pending until the reload tears down the tree;
            // prevents a brief flash of the ErrorBoundary fallback.
            return new Promise<never>(() => {});
          }
          throw error;
        });
      })
  );
}
